import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { TOTPSetup } from "@/components/TOTPSetup";
import FTPSettings from "@/components/FTPSettings";
import { ProfileView } from "@/components/ProfileView";
import { SessionList } from "@/components/SessionList";
import { PageHeader } from "@/components/PageHeader";
import { TwoFactorAuthSection } from "@/components/TwoFactorAuthSection";
import { Shield, User, Lock, Laptop, ChevronRight } from "lucide-react";
import { profileApi } from "@/api/profile";
import { UserProfile, Session } from "@/types/profile";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { handleLogout as performLogout } from "@/utils/logout";

type CachedProfileData = {
  profile: UserProfile | null;
  sessions: Session[];
  totpEnabled: boolean;
  cachedAt: number;
};

const readCachedProfile = (key: string): CachedProfileData | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedProfileData;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCachedProfile = (key: string, data: CachedProfileData) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Ignore cache write failures (e.g., storage quota)
  }
};

const NAV_ITEMS = [
  { id: "profile", label: "Profile", icon: User, description: "Personal information" },
  { id: "security", label: "Security", icon: Lock, description: "2FA & credentials" },
  { id: "sessions", label: "Sessions", icon: Laptop, description: "Active devices" },
];

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userName, userEmail, profile: reduxProfile, sessions: reduxSessions, totpEnabled: reduxTotpEnabled, ftpData: reduxFtpData } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const cacheKey = userEmail ? `profile.cache.${userEmail}` : "profile.cache";
  const cachedData = useMemo(() => readCachedProfile(cacheKey), [cacheKey]);
  const locationState = location.state as { openManageAccess?: boolean } | null;

  
  const [profile, setProfile] = useState<UserProfile | null>(reduxProfile ?? cachedData?.profile ?? null);
  const [sessions, setSessions] = useState<Session[]>(reduxSessions.length > 0 ? reduxSessions : (cachedData?.sessions ?? []));
  const [showTOTPSetup, setShowTOTPSetup] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(reduxTotpEnabled ?? cachedData?.totpEnabled ?? false);
  const [profileImage, setProfileImage] = useState<string>("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeSection, setActiveSection] = useState("profile");
  const [ftpData, setFtpData] = useState<{ status?: number; password?: string; hasPassword?: boolean } | undefined>(reduxFtpData);
  const [autoOpenManageAccess, setAutoOpenManageAccess] = useState(locationState?.openManageAccess || false);

  useEffect(() => {
    const cached = readCachedProfile(cacheKey);
    if (!cached) return;
    setProfile((prev) => prev ?? cached.profile ?? null);
    setSessions((prev) => (prev.length ? prev : cached.sessions ?? []));
    if (cached.totpEnabled) {
      setTotpEnabled(true);
    }
  }, [cacheKey]);

  useEffect(() => {
    if (!profile) return;
    writeCachedProfile(cacheKey, {
      profile,
      sessions,
      totpEnabled,
      cachedAt: Date.now(),
    });
  }, [cacheKey, profile, sessions, totpEnabled]);

  useEffect(() => {
    if (reduxProfile) {
      setProfile(reduxProfile);
    }
  }, [reduxProfile]);

  useEffect(() => {
    if (reduxSessions.length > 0) {
      setSessions(reduxSessions);
    }
  }, [reduxSessions]);

  useEffect(() => {
    if (reduxTotpEnabled !== undefined) {
      setTotpEnabled(reduxTotpEnabled);
    }
  }, [reduxTotpEnabled]);

  useEffect(() => {
    if (reduxFtpData !== undefined) {
      setFtpData(reduxFtpData);
    }
  }, [reduxFtpData]);

  useEffect(() => {
    const loadData = async () => {
      // If we already have all data from Redux, skip API call
      if (reduxProfile && reduxSessions.length > 0 && reduxFtpData !== undefined) {
        setIsRefreshing(false);
        return;
      }

      setIsRefreshing(true);
      try {
        const { profile: profileData, sessions: sessionsData, totpEnabled: totpEnabledFromApi, ftpData: ftpDataFromApi } = await profileApi.getUserData({
          userName,
          userEmail,
        });
        setProfile(profileData);
        setSessions(sessionsData);
        setTotpEnabled(totpEnabledFromApi);
        setFtpData(ftpDataFromApi);

        writeCachedProfile(cacheKey, {
          profile: profileData,
          sessions: sessionsData,
          totpEnabled: totpEnabledFromApi,
          cachedAt: Date.now(),
        });
      } catch (error) {
        toast.error("Failed to load profile data");
      } finally {
        setIsRefreshing(false);
      }
    };
    loadData();
  }, [cacheKey, userName, userEmail, reduxProfile, reduxSessions, reduxFtpData]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    if (locationState?.openManageAccess) {
      setAutoOpenManageAccess(true);
      scrollToSection("profile");
      // Clear location state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [locationState, navigate, location.pathname]);

  // Scrollspy logic
  useEffect(() => {
    const handleScroll = () => {
      const sections = NAV_ITEMS.map(item => item.id);
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
        toast.success("Profile image updated!");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogout = async () => {
    const userId = profile?.id || sessions[0]?.user;
    await performLogout({ userId, sessions, dispatch, navigate });
  };

  const handleProfileUpdate = (updatedProfile: UserProfile) => {
    setProfile(updatedProfile);
    writeCachedProfile(cacheKey, {
      profile: updatedProfile,
      sessions,
      totpEnabled,
      cachedAt: Date.now(),
    });
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      const session = sessions.find((s) => s.id === sessionId);
      if (!session) {
        toast.error("Session not found");
        return;
      }
      await profileApi.revokeSession(sessionId, session.user);
      setSessions(sessions.filter((s) => s.id !== sessionId));
      toast.success("Session revoked successfully");
    } catch (error) {
      toast.error("Failed to revoke session");
    }
  };

  const handleRevokeAllOtherSessions = async () => {
    try {
      const currentSession = sessions.find((s) => s.is_current);
      if (!currentSession) {
        toast.error("Current session not found");
        return;
      }
      const otherSessions = sessions.filter((s) => !s.is_current);
      const revokePromises = otherSessions.map((session) =>
        profileApi.revokeSession(session.id, session.user)
      );
      await Promise.all(revokePromises);
      setSessions(sessions.filter((s) => s.is_current));
      toast.success("All other sessions revoked successfully");
    } catch (error) {
      toast.error("Failed to revoke sessions");
    }
  };

  const handleTOTPComplete = async () => {
    try {
      const { totpEnabled: updatedTotpEnabled } = await profileApi.getUserData({
        userName,
        userEmail,
      });
      setTotpEnabled(updatedTotpEnabled);

      if (updatedTotpEnabled) {
        toast.success("Two-factor authentication enabled successfully!");
      } else {
        toast.warning("2FA setup completed, but status check failed. Please refresh the page.");
      }
    } catch (error) {
      setTotpEnabled(true);
      toast.success("Two-factor authentication enabled successfully!");
    }
  };

  const handleDisable2FA = () => {
    setTotpEnabled(false);
  };

  const fallbackProfile: UserProfile = {
    name: userName || "User",
    email: userEmail || "N/A",
    phone: "N/A",
    role: "N/A",
    permissions: [],
    decoded_permissions: [],
    user_req: [],
  };
  const profileToRender = profile ?? fallbackProfile;

  return (
    <div className="min-h-screen">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">

          {/* LHS Navigation - Enhanced Sticky Sidebar */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-24">
              {/* Navigation */}
              <nav className="space-y-1">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`
                        w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left
                        transition-all duration-300 ease-out group relative
                        ${isActive
                          ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                          : "hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                        }
                      `}
                    >
                      {/* Active indicator bar */}
                      <div className={`
                        absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-full transition-all duration-300
                        ${isActive ? "h-8 bg-primary-foreground/50" : "h-0"}
                      `} />

                      <div className={`
                        p-2 rounded-lg transition-colors duration-200
                        ${isActive ? "bg-primary-foreground/20" : "bg-muted group-hover:bg-background"}
                      `}>
                        <Icon className="w-4 h-4" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{item.label}</p>
                        <p className={`text-xs truncate ${isActive ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                          {item.description}
                        </p>
                      </div>

                      <ChevronRight className={`
                        w-4 h-4 transition-all duration-200
                        ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0"}
                      `} />
                    </button>
                  );
                })}
              </nav>

              {/* Help Text */}
              <div className="mt-8 p-4 rounded-xl bg-muted/30 border border-border/50">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Manage your account settings, security preferences, and active sessions.
                </p>
              </div>
            </div>
          </aside>

          {/* Mobile Navigation */}
          <div className="lg:hidden flex gap-2 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap
                    transition-all duration-200 shrink-0
                    ${isActive
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted"
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Main Content */}
          <main className="flex-1 min-w-0 space-y-16 lg:space-y-20">
            {isRefreshing && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                <span>Refreshing profile data...</span>
              </div>
            )}
            {/* Profile Section */}
            <section id="profile" className="scroll-mt-24">
              {!profile && !isRefreshing && (
                <div className="mb-4 rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
                  Profile details are unavailable right now. Showing static sections and retrying in the background.
                </div>
              )}
              <ProfileView
                profile={profileToRender}
                userName={userName}
                profileImage={profileImage}
                isEditing={false}
                onImageUpload={handleImageUpload}
                onLogout={handleLogout}
                onProfileUpdate={handleProfileUpdate}
                userId={profile?.id}
                autoOpenManageAccess={autoOpenManageAccess}
              />
            </section>

            {/* Security Section */}
            <section id="security" className="scroll-mt-24 space-y-6">
              <PageHeader
                icon={Shield}
                title="Security"
                description="Manage your account security and access"
              />

              <div className="space-y-6">
                <TwoFactorAuthSection
                  totpEnabled={totpEnabled}
                  onDisable={handleDisable2FA}
                  onEnable={() => setShowTOTPSetup(true)}
                />

                <FTPSettings userId={profile?.id} userEmail={userEmail} ftpData={ftpData} />
              </div>
            </section>

            {/* Sessions Section */}
            <section id="sessions" className="scroll-mt-24">
              <SessionList
                sessions={sessions}
                onRevokeSession={handleRevokeSession}
                onRevokeAllOtherSessions={handleRevokeAllOtherSessions}
              />
            </section>

            {/* Bottom Spacer */}
            <div className="h-20" />
          </main>
        </div>
      </div>

      <TOTPSetup
        open={showTOTPSetup}
        onClose={() => setShowTOTPSetup(false)}
        onComplete={handleTOTPComplete}
      />
    </div>
  );
};

export default Profile;
